/**
 *
 * OperationDetail
 *
 * @version 1.0.0
 * @since 26-Jun-2024
 * @author Stefano Servodidio
 */

import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import MerchantFlowCard from '@nexidigital/merchantflowcard'
import MerchantFlowHeadings from '@nexidigital/merchantflowheadings'
import { HelmetWrapper as Helmet } from 'Hoc/HelmetWrapper'
import withRouter from 'Hoc/WithRouterComponent'
import { userPermissionControl, checkGetSubscription } from 'Utility/globalMethods'
import MerchantLayout from 'Components/MerchantLayout'
import OperationPayToCardDetail from 'Components/OperationPayToCardDetail'
import { routesList } from 'Schemas/routesList'
import OperationAdditionalInfo from './components/OperationAdditionalInfo'
import OperationCurrency from './components/OperationCurrency'
import OperationCustomerInfo from './components/OperationCustomerInfo'
import OperationDCC from './components/OperationDCC'
import OperationLoyalty from './components/OperationLoyalty'
import OperationSecurityWarnings from './components/OperationSecurityWarnings'
import OperationSummary from './components/OperationSummary'
import formattedMessage, { captions } from './languageModule'
import './OperationDetail.scss'

const OperationDetail = ({ operation, actions, id, userPermissions, getSubscriptionArray }) => {
  const { operationId: operationIdfromParams, orderId } = useParams()
  const navigate = useNavigate()
  const operationId = id || operationIdfromParams
  const intl = useIntl()

  const loyaltyValues =
    !!operation?.additionalData?.totalRedemptionAmount ||
    !!operation?.additionalData?.XLSTransactionId ||
    !!operation?.additionalData?.totalAwardedPoints ||
    !!operation?.additionalData?.pointsRedeemed ||
    !!operation?.additionalData?.totalRedeemedPoints

  const fieldsArePresent = (lowercaseKey, camelCaseKey) =>
    operation?.additionalData[lowercaseKey] || 
    operation?.additionalData[camelCaseKey]
  
  const hasConversionData = 
    fieldsArePresent('conversionrate', 'conversionRate') &&
    //fieldIsPresent('conversionratedate', 'conversionRateDate') &&
    fieldsArePresent('merchantamount', 'merchantAmount') &&
    fieldsArePresent('merchantcurrency', 'merchantCurrency')

  useEffect(() => {
    if (
      /operationmanagement/i.test(window.location.pathname) &&
      (!operationId || !userPermissionControl('orders.details.operations.info', userPermissions))
    ) {
      navigate(routesList.operationManagement.goToOperationManagement)
    }
    if (operationId) {
      actions.fetchOperationDetails(operationId)
    }
  }, [])

  const header = {
    title: formattedMessage.header,
    onGoBack: () => {
      if (orderId) {
        navigate(routesList.orderManagementRoutes.goToOrderDetails.replace(':orderId', orderId))
      } else {
        navigate(routesList.operationManagement.goToOperationManagement)
      }
    },
    goBackLabel: orderId
      ? intl.formatMessage(captions.goToOrderDetail)
      : intl.formatMessage(captions.goToOperationManagement)
  }

  const accordionElements = [
    {
      title: formattedMessage.summary,
      content: <OperationSummary />,
      disabled: false,
      defaultExpanded: true,
      id: '1'
    },
    {
      title: formattedMessage.additionalInfo,
      content: <OperationAdditionalInfo info={operation?.additionalData} />,
      disabled: false,
      defaultExpanded: true,
      id: '2'
    },
    operation?.operationType === 'OCT' && {
      title: formattedMessage.payToCardDetails,
      content: <OperationPayToCardDetail info={operation?.additionalData} />,
      disabled: false,
      defaultExpanded: true,
      id: '3'
    },
    {
      title: formattedMessage.customerInfo,
      content: <OperationCustomerInfo info={operation?.additionalData} />,
      disabled: false,
      defaultExpanded: true,
      id: '4'
    },
    hasConversionData &&
    !operation?.additionalData.transactiondccid && {
      title: formattedMessage.currency,
      content: <OperationCurrency info={operation} />,
      disabled: false,
      defaultExpanded: true,
      id: '5'
    },
    userPermissionControl('settings.terminals.dcc', userPermissions) &&
    hasConversionData &&
    operation?.additionalData?.transactiondccid && {
      title: formattedMessage.dcc,
      content: <OperationDCC info={operation?.additionalData} />,
      disabled: false,
      defaultExpanded: true,
      id: '6'
    },
    checkGetSubscription(getSubscriptionArray, 'LOYALTY') &&
      userPermissionControl('settings.terminals.vas.loyalty', userPermissions) &&
      loyaltyValues && {
      title: formattedMessage.loyaltyProgram,
      content: <OperationLoyalty info={operation?.additionalData} />,
      disabled: false,
      defaultExpanded: true,
      id: '7'
    },
    {
      title: formattedMessage.securityWarnings,
      content: <OperationSecurityWarnings warnings={operation?.warnings} />,
      disabled: false,
      defaultExpanded: true,
      id: '8'
    }
  ].filter(Boolean)

  // rendered inside OperationDetailModal without merchantLayout
  if (id) {
    return (
      <div className='operationDetail_container modal'>
        {accordionElements.map((element, index) => (
          <MerchantFlowCard
            buttonAriaLabel={intl.formatMessage(captions.openCloseAccordion)}
            defaultExpanded={element.defaultExpanded}
            id={element.id}
            key={index}
            title={element.title}
            disabled={element.disabled}
            accordion>
            {element.content}
          </MerchantFlowCard>
        ))}
      </div>
    )
  }

  return (
    <MerchantLayout header='subroute' headerProps={header} hideSidebar withGutters>
      <Helmet>
        <title>{intl.formatMessage(captions.pageTitle)}</title>
        <meta name='description' content='OperationDetail page' />
      </Helmet>
      <div className='operationDetail_container'>
        <div className='heading'>
          <MerchantFlowHeadings title={formattedMessage.operation(operationId)} />
        </div>
        {accordionElements.map((element, index) => (
          <MerchantFlowCard
            buttonAriaLabel={intl.formatMessage(captions.openCloseAccordion)}
            defaultExpanded={element.defaultExpanded}
            id={element.id}
            key={index}
            title={element.title}
            disabled={element.disabled}
            accordion>
            {element.content}
          </MerchantFlowCard>
        ))}
      </div>
    </MerchantLayout>
  )
}

OperationDetail.propTypes = {
  actions: PropTypes.object,
  operation: PropTypes.object,
  id: PropTypes.string,
  userPermissions: PropTypes.array,
  getSubscriptionArray: PropTypes.array
}

OperationDetail.defaultProps = {
  actions: {},
  operation: null,
  id: '',
  userPermissions: [],
  getSubscriptionArray: []
}

export default withRouter(OperationDetail)
